// ENG-7053 Slice C — live-session tool-presence verdict (pure core).
//
// Decides whether an integration's MCP tools are actually BOUND in the running
// agent session, by composing the signals the manager already has:
//
//   • stdio MCP servers (incl. the ENG-6859 remote-OAuth stdio proxy): a server
//     is bound iff it has a live child process. `findMissingMcpServers`
//     (mcp-presence-reaper) already computes the declared-stdio keys with ZERO
//     live children — exactly the "tools fell off the session" symptom. A key in
//     that missing-set ⇒ not bound.
//   • URL/HTTP remote MCP servers: no child process to find, so presence is an
//     HTTP `initialize → tools/list` reachability check (`probeMcpHttp`). Tools
//     bound ⇒ the endpoint answers; unreachable/unauthorized ⇒ not bound.
//
// This is the OBSERVATIONAL signal for Slice C (flag AGT_SESSION_TOOL_PROBE_ENABLED).
// Slice A consumes the same verdict to trigger an in-session rebind. Kept pure
// (no `ps`/network here) so the decision logic is unit-testable; the manager
// supplies the assembled inputs.

import type { McpConfig } from './stale-mcp-reaper.js';

/** Verdict persisted to `<scope>_integrations.last_session_tool_bind_status`. */
export type SessionToolBindStatus =
  | 'bound'
  | 'missing'
  | 'unreachable'
  | 'unknown'
  /**
   * ENG-9114: this integration contributes no `.mcp.json` server, so "are its
   * tools bound in the session?" is not a question about it. Permanent and
   * correct, NOT a failure — the opposite of the `null` it used to leave
   * behind, which readers reliably misread as "broken and nobody looked".
   *
   * Decided by `isSessionToolBindCandidate` from the toolkit's delivery
   * columns, NOT by the absence of resolved keys: an MCP integration whose
   * `.mcp.json` entry has not been rendered yet also resolves to zero keys,
   * and that one really is "not probed yet" and must stay `unknown`.
   */
  | 'not_applicable';

/** How Claude Code talks to a declared MCP server. */
export type McpServerKind = 'stdio' | 'http' | 'oauth-proxy' | 'unknown';

/**
 * Classify a declared `.mcp.json` server entry.
 *
 *   • `oauth-proxy` - the ENG-6859 remote-OAuth stdio proxy (granola,
 *     brand-ninja). It has a `command` (a spawned child) but is really a shim in
 *     front of a REMOTE endpoint (`AGT_REMOTE_MCP_URL`) whose Bearer is read live
 *     from a token file (`AGT_REMOTE_MCP_TOKEN_VAR`). Its tools are bound only if
 *     BOTH the child is alive AND the upstream still answers `tools/list`, so it
 *     needs the stdio child-liveness leg AND the http reachability leg. Detected
 *     by the proxy's baked env so a plain native stdio server is not misclassified
 *     (ENG-7318: keying purely on `command` classified this `stdio`, so an
 *     alive-child/stale-upstream proxy falsely read `bound` and never rebound).
 *   • `stdio` - a plain spawned child (native MCP): bound iff the child is alive.
 *   • `http` - a `url` (no `command`) remote Streamable-HTTP/SSE endpoint Claude
 *     Code dials directly (no child).
 *
 * Mirrors the stdio test in `findMissingMcpServers`.
 */
export function classifyMcpServer(entry: unknown): McpServerKind {
  if (!entry || typeof entry !== 'object') return 'unknown';
  const e = entry as { command?: unknown; url?: unknown; env?: unknown };
  if (typeof e.command === 'string') {
    const env = e.env;
    if (
      env &&
      typeof env === 'object' &&
      typeof (env as Record<string, unknown>)['AGT_REMOTE_MCP_URL'] === 'string' &&
      typeof (env as Record<string, unknown>)['AGT_REMOTE_MCP_TOKEN_VAR'] === 'string'
    ) {
      return 'oauth-proxy';
    }
    return 'stdio';
  }
  if (typeof e.url === 'string') return 'http';
  return 'unknown';
}

/**
 * Split an integration's declared MCP server keys into stdio / http / oauth-proxy
 * using the agent's parsed `.mcp.json`. Keys with no entry (or an unclassifiable
 * one) are dropped - they can't be probed for presence either way.
 *
 * `proxy` keys (the ENG-6859 remote-OAuth stdio proxy) are a hybrid: they need
 * the stdio child-liveness leg (a dead child = `missing`) AND the http `tools/list`
 * reachability leg (an alive child whose upstream stopped answering = `unreachable`),
 * so they're kept separate rather than folded into `stdio` (ENG-7318).
 */
export function splitServerKeysByKind(
  mcpJson: McpConfig | null | undefined,
  serverKeys: readonly string[],
): { stdio: string[]; http: string[]; proxy: string[] } {
  const servers = mcpJson?.mcpServers ?? {};
  const stdio: string[] = [];
  const http: string[] = [];
  const proxy: string[] = [];
  for (const key of serverKeys) {
    const kind = classifyMcpServer((servers as Record<string, unknown>)[key]);
    if (kind === 'stdio') stdio.push(key);
    else if (kind === 'http') http.push(key);
    else if (kind === 'oauth-proxy') proxy.push(key);
  }
  return { stdio, http, proxy };
}

export interface SessionToolBindInput {
  /** Declared plain-stdio MCP server keys (native MCP) backing this integration. */
  stdioServerKeys: readonly string[];
  /** Declared URL/HTTP MCP server keys backing this integration. */
  httpServerKeys: readonly string[];
  /**
   * Declared OAuth-proxy stdio server keys (ENG-6859 remote-OAuth proxy, e.g.
   * granola) backing this integration. A hybrid: bound iff BOTH the proxy child
   * is alive (via `missingStdioKeys`) AND the upstream answers `tools/list` (via
   * `httpReachable`, reconstructed from the proxy env). An alive child whose
   * upstream stopped answering ⇒ `unreachable`, not a false `bound` (ENG-7318).
   */
  proxyServerKeys: readonly string[];
  /**
   * Stdio/proxy server keys found to have NO live child (from
   * `findMissingMcpServers`, which enumerates every `command` entry incl. the
   * OAuth proxy). Any of this integration's stdio OR proxy keys appearing here ⇒
   * its backing child died and its tools fell out of the session.
   */
  missingStdioKeys: ReadonlySet<string>;
  /**
   * Per-key reachability of the `tools/list` probe for http AND oauth-proxy keys:
   * true = answered (upstream healthy), false = unreachable/unauthorized, absent =
   * not probed this cycle.
   */
  httpReachable: ReadonlyMap<string, boolean>;
  /**
   * ENG-9048 AC3: server keys whose CHILD IS ALIVE but whose transport has
   * stopped carrying traffic.
   *
   * `missingStdioKeys` cannot see this. It asks `ps` whether the declared stdio
   * server has a live child, and through the 3h47m Slack outage the
   * `slack-channel` process was alive the entire time — pid alive, lock fresh.
   * The reaper therefore evaluated `healthy` on every 10s poll, correctly, by
   * its own definition. Detecting that failure needed a new INPUT, not a
   * different threshold, and this is it.
   *
   * Populated only from a positive, FRESH liveness signal published by the
   * server itself; absence of evidence must never land a key in here, because
   * the downstream consumer SIGTERMs the child.
   */
  transportDownKeys?: ReadonlySet<string>;
  /**
   * The `.mcp.json` `mcpServers` keys the running session actually loaded at
   * spawn (ENG-7263, from the session's persisted spawn-time snapshot). HTTP/
   * remote MCP servers have no child process, so a reachable endpoint proves the
   * endpoint+creds are good but NOT that THIS session loaded the (possibly newly
   * added) server key. This set is the HTTP analogue of stdio child-liveness:
   * only an HTTP key present here can promote to `bound`. A reachable key NOT in
   * the set (added after spawn, awaiting restart) or an empty/unknown set keeps
   * the integration out of `bound` - so we never claim "live" before the agent's
   * restart actually loaded it. Replaces the old `.mcp.json` file-mtime gate,
   * which the manager's post-spawn re-renders made perpetually false. Irrelevant
   * to stdio keys, whose live child is itself the load proof.
   */
  sessionLoadedServerKeys: ReadonlySet<string>;
  /**
   * ENG-8448: true when the running session is still inside its cold-start grace
   * window (`< DEFAULT_COLD_START_GRACE_MS` since spawn). A stdio/proxy child can
   * take seconds (CLI-bundled) to tens of seconds (npx-delivered, e.g.
   * cloud-broker) to appear in `ps` after a (re)spawn, so a bind probe that races
   * that window sees "no live child" for a broker that is simply still launching.
   * Within the window a would-be `missing` verdict is downgraded to `unknown`
   * (dropped, never counted toward the ENG-7916 bind-failure quarantine) -
   * mirroring the presence-reaper's cold-start grace, which suppresses the
   * symmetric reap. Defaults to false, so the on-demand / unknown-start paths
   * behave exactly as before.
   */
  coldStartActive?: boolean;
  /**
   * ENG-10601: THE WITNESS. Did this session manage to spawn ANY declared stdio
   * child at all?
   *
   * A session wedged before it starts - parked on Claude Code's login picker, or
   * on an unclaimed modal (ENG-9932) - spawns no MCP child whatsoever, so EVERY
   * declared key reads as missing on EVERY cycle. Three of those in a row latch
   * an ENG-7916 quarantine that a human then has to clear, which is how a
   * transient login problem leaves a permanent integration outage behind it. The
   * integration was never broken; the SESSION never started, and "no tools
   * bound" is evidence about the session, not about the integration.
   *
   * So require a witness: at least one declared stdio/proxy server has a live
   * child. With one, a specific key being missing really is about that key.
   * Without one, there is nothing to attribute the absence TO, and a would-be
   * `missing` is downgraded to `unknown` - dropped by the runner, never counted
   * toward the quarantine.
   *
   * This is the ENG-10500 witness gate, which fixed exactly this fault on the
   * presence-reaper's quarantine lane (`mcp-presence-reaper.ts:1219`). The
   * session-tool-bind lane was never given the same gate. The two now agree.
   *
   * WITHHOLDING IS NOT TERMINAL: the next cycle with a live witness decides
   * normally. Defaults to `true` - a caller that cannot observe child liveness
   * (and therefore passes an empty `missingStdioKeys`) is not asserting the
   * session is dead, and must behave exactly as it did before this shipped.
   */
  anyStdioServerLive?: boolean;
}

/**
 * The verdict. `unknown` is deliberate, not a failure: it means we had no
 * probeable signal (no declared MCP servers, an HTTP key we didn't get to this
 * cycle, or a reachable HTTP key the running session hasn't loaded yet) - so an
 * observer (and Slice A) must NOT treat it as "broken".
 *
 *   missing      - a backing stdio OR oauth-proxy child is gone (the classic "fell off")
 *   unreachable  - a backing HTTP MCP, or an alive oauth-proxy's upstream, didn't
 *                  answer tools/list
 *   bound        - every backing server is present in the running session
 *   unknown      - nothing conclusive to decide on yet
 *
 * Negative signals win (missing > unreachable > bound) so a half-down
 * integration never reports healthy.
 *
 * For a pure-HTTP integration, `bound` additionally requires each backing HTTP
 * key to be in `sessionLoadedServerKeys` - reachability alone can't prove the
 * running session loaded the server, so a reachable key the session didn't load
 * at spawn reports `unknown` (pending) rather than a false `bound`. A live stdio
 * child is itself proof the session loaded that server, so a mixed/stdio
 * integration doesn't need the set.
 */
export function computeSessionToolBindStatus(input: SessionToolBindInput): SessionToolBindStatus {
  const {
    stdioServerKeys,
    httpServerKeys,
    proxyServerKeys,
    missingStdioKeys,
    httpReachable,
    sessionLoadedServerKeys,
    coldStartActive = false,
    anyStdioServerLive = true,
    transportDownKeys,
  } = input;

  if (stdioServerKeys.length === 0 && httpServerKeys.length === 0 && proxyServerKeys.length === 0)
    return 'unknown';

  // A backing stdio OR oauth-proxy child gone = tools not bound in the session.
  // (`missingStdioKeys` enumerates every `command` entry, proxy included.) This
  // is the Phil/ENG-7301 case for the proxy: the child never launched / died.
  if (
    stdioServerKeys.some((k) => missingStdioKeys.has(k)) ||
    proxyServerKeys.some((k) => missingStdioKeys.has(k))
  ) {
    // ENG-8448: within the cold-start grace window a still-spawning child reads as
    // "no live child" but isn't a real failure - report `unknown` (dropped by the
    // runner, never counted toward the ENG-7916 quarantine) instead of a false
    // `missing`, so a forced post-restart probe (ENG-7429) can't spuriously
    // quarantine a broker that simply hasn't finished launching. Outside the
    // window (or when the spawn time is unknown) this stays `missing`.
    // ENG-10601: and the witness. No declared stdio child is live at all, so
    // this session spawned nothing - the absence is about the session, not this
    // integration. Same downgrade, same reason as the cold-start grace above:
    // report `unknown` rather than feed a false `missing` to the quarantine.
    return coldStartActive || !anyStdioServerLive ? 'unknown' : 'missing';
  }

  // ENG-9048 AC3: an alive child whose TRANSPORT is dead. Ordered here
  // deliberately — after the missing-child branch, because a dead child is the
  // stronger signal and the documented precedence is missing > unreachable >
  // bound, and before the reachability legs, because those probe an upstream
  // over HTTP and this failure is invisible to HTTP (through the outage the
  // Slack Web API was healthy the whole time; only the inbound WebSocket was
  // dead, which is exactly why nothing noticed).
  if (transportDownKeys && transportDownKeys.size > 0) {
    if (
      stdioServerKeys.some((k) => transportDownKeys.has(k)) ||
      httpServerKeys.some((k) => transportDownKeys.has(k)) ||
      proxyServerKeys.some((k) => transportDownKeys.has(k))
    ) {
      return 'unreachable';
    }
  }

  // OAuth-proxy keys: the child is alive (checked above), so now exercise the
  // upstream through the reconstructed endpoint. An alive proxy child whose
  // upstream no longer answers `tools/list` is the ENG-7318 "healthy connection,
  // but the running session's tools went stale under a token refresh" case -
  // report `unreachable` instead of the false `bound` the old command-only
  // classification produced. A live child + reachable upstream is positive
  // evidence; an unprobed/transient miss (`undefined`) does NOT penalise (the
  // live child alone is at least as healthy as the prior child-only verdict).
  for (const k of proxyServerKeys) {
    if (httpReachable.get(k) === false) return 'unreachable';
  }

  // Walk the HTTP keys. Reachability only carries an in-session verdict for a key
  // the running session actually LOADED (its key was in the spawn-time
  // `.mcp.json` snapshot). For a key NOT in the snapshot - added after spawn,
  // awaiting restart, or snapshot unavailable - the session can't use it yet
  // regardless of endpoint state, so it stays "unconfirmed" (unknown/pending);
  // we do NOT report `unreachable` for a server the session never loaded
  // (CodeRabbit, PR #2893). For a loaded key: a down endpoint is a hard negative
  // (unreachable), reachable is positive evidence, unprobed stays unconfirmed.
  let anyHttpUnconfirmed = false;
  for (const k of httpServerKeys) {
    if (!sessionLoadedServerKeys.has(k)) {
      anyHttpUnconfirmed = true; // session didn't load it; reachability is moot
      continue;
    }
    const reachable = httpReachable.get(k);
    if (reachable === false) return 'unreachable'; // loaded, but endpoint is down
    if (reachable === undefined) anyHttpUnconfirmed = true; // loaded, not probed this cycle
  }

  // All stdio/proxy children present and no upstream hard-down. A live stdio or
  // oauth-proxy child proves session-load, so if any such key backs this
  // integration we're bound. For a pure-HTTP integration we only claim bound when
  // every HTTP key is confirmed reachable-and-loaded; an unconfirmed one (unprobed,
  // or reachable-but-pre-restart) stays 'unknown' rather than a premature 'bound'.
  if (anyHttpUnconfirmed && stdioServerKeys.length === 0 && proxyServerKeys.length === 0)
    return 'unknown';
  return 'bound';
}
